import dynamic from 'next/dynamic';
import classNames from '@/common/utils/classNames';
import Text from '@/components/atom/text';
import { useRouter } from 'next/router';
import { HTMLAttributes, ReactNode } from 'react';
import Skeleton from '../atom/skeleton';
// Lazy: rendered only for signed-in users, but a static import put the whole launcher's CSS/JS on every page.
const LauncherBlockNotifications = dynamic(() => import('.plasmic/LauncherBlockNotifications'));
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';

interface AppBarProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  backButton?: Boolean;
  onBackClick?: () => void;
  titleLoading?: Boolean;
  actionButton?: ReactNode;
  notification?: boolean;
  /** Shown before the title (e.g. a small avatar). */
  titleLeading?: ReactNode;
  /** Collapsing title: hidden until true (profile pages reveal it once the page's own heading scrolls away). */
  titleRevealed?: boolean;
  /** Settings-style bar (Telegram/Eitaa): 20px medium title. */
  largeTitle?: boolean;
}

export const AppBar = ({
  title,
  backButton,
  notification = true,
  onBackClick,
  titleLoading = false,
  className,
  actionButton,
  titleLeading,
  titleRevealed,
  largeTitle,
  ...rest
}: AppBarProps) => {
  const router = useRouter();
  const isLogin = useUserInfoStore(state => state.isLogin);
  const handleBack = onBackClick ?? (() => router.back());
  // New Android app: app-style bar (Divar/Digikala/Telegram) — 48px back target, 16px medium title, no divider.
  const isApp = useIsNewAppShell();
  return (
    <div
      data-app-bar=""
      className={classNames(
        'flex sticky top-0 items-center min-h-14 h-14 bg-white z-40',
        isApp && 'text-[#222]',
        isApp ? 'px-5' : 'px-6 border-b border-[#EBECEE]',
        isApp && backButton && 'ps-1',
        // Once a collapsing title is in, content scrolls under the bar: a hairline separates them.
        isApp && titleRevealed && 'shadow-[0_1px_0_rgba(0,0,0,0.06)]',
        // New app: always shown — pages hide it on the website with `hidden pwa:!flex`.
        isApp
          ? className
              ?.split(' ')
              .filter(c => c !== 'hidden')
              .join(' ')
          : className,
      )}
      {...rest}
    >
      {backButton && (
        <div
          role="button"
          aria-label="بازگشت"
          onClick={handleBack}
          className={classNames(
            'cursor-pointer',
            isApp
              ? classNames(
                  'flex h-12 w-12 items-center justify-center rounded-full active:bg-black/[0.06]',
                  titleLeading ? 'me-0.5' : 'me-3',
                )
              : 'ml-2 -mr-3',
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={isApp ? 1.5 : 2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {isApp ? (
              // Same outline family/weight as the profile bar icons (share, save, views).
              <path d="M14.43 5.93 20.5 12l-6.07 6.07M3.5 12h16.83" />
            ) : (
              <>
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </>
            )}
          </svg>
        </div>
      )}

      {titleLoading ? (
        <Skeleton w="7rem" h="1rem" rounded="full" />
      ) : (
        <div
          aria-hidden={titleRevealed === false || undefined}
          className={classNames(
            'flex min-w-0 items-center gap-2',
            titleRevealed !== undefined && 'transition-[opacity,transform] duration-200 ease-out',
            titleRevealed === false && 'pointer-events-none translate-y-1 opacity-0',
          )}
        >
          {titleLeading}
          <Text
            fontWeight={isApp ? 'semiBold' : 'bold'}
            fontSize="sm"
            className={classNames(
              'line-clamp-1',
              // collapsing (profile) titles are a step smaller than page titles
              isApp &&
                (largeTitle
                  ? '!text-[18px] !leading-7 text-[#222]'
                  : titleRevealed !== undefined
                  ? '!text-[16px] !leading-6 text-[#222]'
                  : '!text-[18px] !leading-7 text-[#222]'),
            )}
          >
            {title}
          </Text>
        </div>
      )}

      {actionButton && (
        <div className="cursor-pointer left-5 absolute flex gap-3 items-center">
          {isLogin && notification && <LauncherBlockNotifications />}
          {actionButton}
        </div>
      )}
    </div>
  );
};

export default AppBar;
